<script lang="ts">
	import czechIcon from "@assets/images/cz.svg?raw";
	import englishIcon from "@assets/images/en.svg?raw";
	import Icon from "@components/common/Icon.svelte";
	import Link from "@components/common/Link.svelte";
	import { isMenuActive } from "@utils/isMenuActive.js";
	import { slide } from "svelte/transition";
	import closeIcon from "@core/assets/icons/close.svg?raw";
	import menuIcon from "@core/assets/icons/menu.svg?raw";
	import logoUrl from "@core/assets/logo.svg";
	import { Locale } from "@core/schema/Locale.js";
	import type { MenuItem } from "@core/schema/MenuItem.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";

	const { menu }: { menu: MenuItem[] } = $props();
	const { tenant, pagePath } = getGlobalTemplateProps();

	let open = $state(false);

	const localeIconMap: Record<Locale, string> = {
		[Locale.cs]: czechIcon,
		[Locale.en]: englishIcon,
	};
	const localeNameMap: Record<Locale, string> = {
		[Locale.cs]: "Čeština",
		[Locale.en]: "English",
	};

	const currentPath = $derived(pagePath[tenant.locale] ?? "/");
</script>

<header class="grid-container sticky top-3 z-50 mt-3 md:top-4 md:mt-4 md:mb-12">
	<div
		class="ff-fade-in border-ink-200 shadow-card flex items-center justify-between gap-3 rounded-2xl border bg-white/85 px-3 py-2 backdrop-blur-md sm:px-5 sm:py-2.5"
	>
		<!-- Brand: DiscDog Klub ČR mark + FlyFetch platform wordmark -->
		<a href="/" class="group flex shrink-0 items-center gap-2.5" aria-label="FlyFetch — DiscDog Klub ČR">
			<img
				src={logoUrl}
				alt="DiscDog Klub České republiky"
				width="474"
				height="422"
				class="ease-spring h-12 w-auto transition-transform duration-200 group-hover:scale-105 sm:h-14"
			/>
			<span
				class="font-display text-ink-950 text-[1.5rem] font-extrabold uppercase leading-none tracking-tight sm:text-[1.7rem]"
			>
				FlyFetch
			</span>
		</a>

		<!-- Desktop nav -->
		<nav class="hidden items-center gap-1 lg:flex">
			{#each menu as menuItem, index (index)}
				<Link
					class={[
						"rounded-md px-3.5 py-2 text-sm font-semibold tracking-wide transition-colors duration-150",
						isMenuActive(menuItem, currentPath)
							? "text-secondary bg-secondary-50"
							: "text-ink-700 hover:text-secondary hover:bg-ink-50",
					]}
					href={menuItem.href}
					target={menuItem.target}
				>
					{menuItem.name}
				</Link>
			{/each}
			{#each Object.values(Locale).filter((locale) => locale !== tenant.locale) as locale (locale)}
				<Link
					class="hover:bg-ink-50 ml-1 rounded-md p-2 transition-colors"
					href={pagePath[locale]}
					aria-label={localeNameMap[locale]}
				>
					<Icon class="h-5 w-5" icon={localeIconMap[locale]} />
				</Link>
			{/each}
		</nav>

		<!-- Mobile toggle -->
		<button
			type="button"
			class="text-ink-800 hover:bg-ink-50 -mr-0.5 flex h-11 w-11 items-center justify-center rounded-md transition-colors lg:hidden"
			aria-label="Menu"
			aria-expanded={open}
			onclick={() => (open = !open)}
		>
			<Icon class="h-6 w-6" icon={open ? closeIcon : menuIcon} />
		</button>
	</div>

	<!-- Mobile menu -->
	{#if open}
		<nav
			class="border-ink-200 shadow-card mt-2 flex flex-col gap-1 rounded-2xl border bg-white/95 p-3 backdrop-blur-md lg:hidden"
			transition:slide={{ duration: 200 }}
		>
			{#each menu as menuItem, index (index)}
				<Link
					class={[
						"rounded-lg px-4 py-3 text-base font-semibold transition-colors",
						isMenuActive(menuItem, currentPath) ? "text-secondary bg-secondary-50" : "text-ink-800 hover:bg-ink-50",
					]}
					href={menuItem.href}
					target={menuItem.target}
					onclick={() => (open = false)}
				>
					{menuItem.name}
				</Link>
			{/each}
			<div class="border-ink-200 mt-1 flex items-center gap-2 border-t pt-2">
				{#each Object.values(Locale).filter((locale) => locale !== tenant.locale) as locale (locale)}
					<Link
						class="text-ink-700 hover:bg-ink-50 flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-semibold"
						href={pagePath[locale]}
						onclick={() => (open = false)}
					>
						<Icon class="h-5 w-5" icon={localeIconMap[locale]} />
						{localeNameMap[locale]}
					</Link>
				{/each}
			</div>
		</nav>
	{/if}
</header>
